import {
  Box,
  Button,
  ButtonGroup,
  Center,
  Divider,
  Editable,
  EditableInput,
  EditablePreview,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useEditableControls,
} from '@chakra-ui/react'
import { useCallback, useEffect, useState } from 'react'
import { MdDeleteForever } from 'react-icons/md'
import rootStore from '../../../../store/RootStore'
import { CheckIcon, CloseIcon, EditIcon } from '@chakra-ui/icons'

import ListBox from '../../../ui/ListBox'
import Message from '../Message/Message'
import { observer } from 'mobx-react'
import { useFetchFilters } from '../../../../pages/WizardPage/hooks/useFetchFilters'

const FilterConfig = observer(({ isOpen, onClose }) => {
  const {
    wizardStore: {
      selectedSection,
      updateReportSectionFilters,
      setSaved,
      setError,
      sourceId,
    },
  } = rootStore

  const { data: filters, isLoading, error } = useFetchFilters(sourceId)

  const [configTemp, setConfigTemp] = useState({
    filtersSelected: [],
    filtersAvailable: [],
  })

  useEffect(() => {
    if (!selectedSection || !filters) {
      onClose()
      return
    }

    const allFilters = Object.entries(filters).map(([key, value]) => ({
      id: key,
      name: `${key.charAt(0).toUpperCase()}${key.slice(1)}`,
      options: Object.values(value).map(item => ({
        id: item.id,
        text: item.longName,
        selected: false,
        default: false,
      })),
    }))

    const filtersAvailable = allFilters.filter(
      item => !selectedSection?.filters?.includes(item.name),
    )
    const filtersSelected = allFilters.filter(item =>
      selectedSection?.filters?.includes(item.name),
    )
    setConfigTemp({
      filtersAvailable,
      filtersSelected,
    })

    return () => {
      setSaved(null)
      setError(null)
    }
  }, [
    filters,
    onClose,
    selectedSection,
    selectedSection?.filters,
    setError,
    setSaved,
  ])

  const handleChangeFilters = () => {
    setConfigTemp({ ...configTemp })
  }

  const handleEditSection = useCallback(
    section => {
      let config = { ...configTemp }
      config.section = section
      setConfigTemp({ ...config })
    },
    [configTemp],
  )

  const handleAddFilter = useCallback(() => {
    let config = { ...configTemp }
    const selectedFilters = configTemp.filtersAvailable?.filter(
      item => item.selected,
    )
    config.filtersSelected = config.filtersSelected.concat(selectedFilters)
    config.filtersAvailable = config.filtersAvailable?.filter(i => !i.selected)
    setConfigTemp(config)
  }, [configTemp])

  const handleRemoveFilter = (filter, index) => {
    let config = { ...configTemp }
    filter.selected = false
    config.filtersSelected.splice(index, 1)
    config.filtersAvailable?.push(filter)
    setConfigTemp(config)
  }

  const handleSaveConfig = useCallback(() => {
    if (!selectedSection?.id) return
    updateReportSectionFilters(
      selectedSection.id,
      configTemp.filtersSelected,
      configTemp.section,
    )
    if (!error) {
      onClose()
    }
  }, [
    selectedSection?.id,
    updateReportSectionFilters,
    configTemp.filtersSelected,
    configTemp.section,
    error,
    onClose,
  ])

  const handleCloseMessage = useCallback(() => {
    setError(null)
    setSaved(null)
  }, [setError, setSaved])

  return (
    <Modal
      size="5xl"
      isCentered
      onClose={onClose}
      isOpen={isOpen}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Heading
            as="h2"
            m="12px">
            <Flex
              flexDirection="row"
              alignItems="center">
              Section:
              <Editable
                textAlign="center"
                defaultValue={selectedSection?.title}
                fontSize="3xl"
                isPreviewFocusable={false}
                display="flex"
                flexDirection="row"
                marginLeft="20px"
                onSubmit={handleEditSection}>
                <EditablePreview marginRight="20px" />
                <Input as={EditableInput} />
                <EditableControls />
              </Editable>
            </Flex>
          </Heading>
        </ModalHeader>
        {/*<ModalCloseButton />*/}
        <ModalBody>
          <Flex
            flexDirection="column"
            m="16px">
            {isLoading && <Box>Loading...</Box>}
            {error && (
              <Message
                message={error}
                onClose={handleCloseMessage}
              />
            )}
            <Flex
              height="60vh"
              flexDirection="row"
              overflowX="auto">
              <Flex flexDirection="column">
                <ListBox
                  title="Filters"
                  list={configTemp.filtersAvailable}
                  textField="name"
                  width="200px"
                  minW="200px"
                  onClick={handleChangeFilters}
                />
                <Flex
                  flexDirection="row-reverse"
                  marginTop="20px">
                  <Button
                    variant="primaryOrange"
                    onClick={handleAddFilter}>
                    Add
                  </Button>
                </Flex>
                <Text
                  width="200px"
                  marginTop="80px">
                  Note: if filter not used at the section level, it will appear
                  at the chart level.
                </Text>
              </Flex>
              <Center
                minHeight="100%"
                marginLeft="20px">
                <Divider
                  orientation="vertical"
                  borderColor="black"
                />
              </Center>
              <Flex
                flexDirection="column"
                justifyContent="space-between">
                <Flex flexDirection="row">
                  <FiltersSelected
                    filtersSelected={configTemp.filtersSelected}
                    onRemoveFilter={handleRemoveFilter}
                    onChangeFilters={handleChangeFilters}
                  />
                </Flex>
                <Text marginLeft="20px">
                  Right click to make a value the{' '}
                  <Text
                    as="strong"
                    textDecoration="underline">
                    default
                  </Text>
                </Text>
              </Flex>
            </Flex>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <HStack>
            <Button
              variant="outline"
              onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="orange"
              onClick={handleSaveConfig}>
              Save
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
})

const FiltersSelected = ({
  filtersSelected,
  onRemoveFilter,
  onChangeFilters,
}) => {
  return filtersSelected.map((filter, index) => (
    <Flex
      key={index}
      flexDirection="column"
      width="200px"
      minW="200px"
      marginLeft="20px">
      <ListBox
        title={
          <Flex
            flexDirection="row"
            justifyContent="space-between">
            {filter.name}
            <MdDeleteForever
              size={20}
              title="remove"
              cursor="pointer"
              style={{
                minWidth: '20px',
              }}
              onClick={() => {
                onRemoveFilter(filter, index)
              }}
            />
          </Flex>
        }
        list={filter.options}
        textField="text"
        maxH="200px"
        onContextMenu={onChangeFilters}
      />
    </Flex>
  ))
}

function EditableControls() {
  const {
    isEditing,
    getSubmitButtonProps,
    getCancelButtonProps,
    getEditButtonProps,
  } = useEditableControls()

  return isEditing ? (
    <ButtonGroup
      justifyContent="center"
      size="sm">
      <IconButton
        icon={<CheckIcon />}
        {...getSubmitButtonProps()}
      />
      <IconButton
        icon={<CloseIcon />}
        {...getCancelButtonProps()}
      />
    </ButtonGroup>
  ) : (
    <Flex
      justifyContent="center"
      alignItems="center">
      <IconButton
        size="sm"
        icon={<EditIcon />}
        {...getEditButtonProps()}
      />
    </Flex>
  )
}

EditableControls.displayName = 'EditableControls'
FilterConfig.displayName = 'FilterConfig'

export default FilterConfig
